import React, { useEffect, useState } from 'react';
import { ShieldCheck, Phone, Mail, Shield } from 'lucide-react';
import { useFormContext } from '../FormContext';
import { FormStep } from '../FormStep';
import { InfoBox } from '../InfoBox';
import { FormButtons } from '../FormButtons';
import { generateMathCaptcha } from '../../../utils/antiSpam';
import { isSpamEmail } from '../../../utils/antiSpam';
export const PersonalDetailsStep: React.FC = () => {
  const {
    formData,
    handleChange,
    goToPreviousStep,
    goToNextStep,
    updateFormData
  } = useFormContext();
  const [captcha, setCaptcha] = useState(generateMathCaptcha());
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [formError, setFormError] = useState('');
  // Regenerate captcha when the component mounts
  useEffect(() => {
    setCaptcha(generateMathCaptcha());
    // Set a timestamp for form start
    updateFormData({
      formStartTime: Date.now(),
      formToken: Date.now().toString()
    });
  }, []);
  const handleCaptchaChange = e => {
    setCaptchaAnswer(e.target.value);
  };
  const refreshCaptcha = () => {
    setCaptcha(generateMathCaptcha());
    setCaptchaAnswer('');
  };
  const validateAndProceed = () => {
    // Check if captcha is correct
    if (parseInt(captchaAnswer) !== captcha.answer) {
      setFormError('De captcha is onjuist. Probeer opnieuw.');
      refreshCaptcha();
      return;
    }
    // Check for spam email domains
    if (formData.email && isSpamEmail(formData.email)) {
      setFormError('Dit e-mailadres is niet toegestaan. Gebruik een geldig e-mailadres.');
      return;
    }
    // Clear any errors and proceed
    setFormError('');
    goToNextStep();
  };
  return <FormStep title="Uw contactgegevens" description="Vul uw gegevens in zodat we contact met u kunnen opnemen." isActive={true} data-id="element-1046">
      {formError && <div className="mb-6 p-4 bg-red-500/20 border-l-4 border-red-500 text-white rounded" data-id="element-1047">
          <p className="font-medium" data-id="element-1048">Er is een probleem opgetreden</p>
          <p data-id="element-1049">{formError}</p>
        </div>}
      {/* Honeypot field - hidden from real users but bots will fill it */}
      <div className="opacity-0 absolute top-0 left-0 h-0 w-0 -z-10 overflow-hidden" data-id="element-1050">
        <label htmlFor="website" data-id="element-1051">Website</label>
        <input type="text" id="website" name="honeypot" value={formData.honeypot || ''} onChange={handleChange} tabIndex={-1} autoComplete="off" data-id="element-1052" />
      </div>
      <div className="space-y-6 mb-8" data-id="element-1053">
        <div data-id="element-1054">
          <label htmlFor="name" className="block text-white/90 font-medium mb-2" data-id="element-1055">
            Naam *
          </label>
          <div className="relative" data-id="element-1056">
            <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} required className="w-full px-4 py-3 rounded-md border border-white/30 bg-white/10 focus:outline-none focus:ring-2 focus:ring-[#CDA735] focus:border-transparent text-white placeholder-white/50" placeholder="Uw volledige naam" data-id="element-1057" />
          </div>
        </div>
        <div data-id="element-1058">
          <label htmlFor="phone" className="block text-white/90 font-medium mb-2" data-id="element-1059">
            Telefoonnummer *
          </label>
          <div className="relative" data-id="element-1060">
            <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} required className="w-full px-4 py-3 rounded-md border border-white/30 bg-white/10 focus:outline-none focus:ring-2 focus:ring-[#CDA735] focus:border-transparent text-white placeholder-white/50" placeholder="06 12345678" data-id="element-1061" />
            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none" data-id="element-1062">
              <Phone className="h-5 w-5 text-white/50" data-id="element-1063" />
            </div>
          </div>
        </div>
        <div data-id="element-1064">
          <label htmlFor="email" className="block text-white/90 font-medium mb-2" data-id="element-1065">
            E-mailadres *
          </label>
          <div className="relative" data-id="element-1066">
            <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required className="w-full px-4 py-3 rounded-md border border-white/30 bg-white/10 focus:outline-none focus:ring-2 focus:ring-[#CDA735] focus:border-transparent text-white placeholder-white/50" placeholder="uw@email.nl" data-id="element-1067" />
            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none" data-id="element-1068">
              <Mail className="h-5 w-5 text-white/50" data-id="element-1069" />
            </div>
          </div>
        </div>
        <div data-id="element-1070">
          <label htmlFor="message" className="block text-white/90 font-medium mb-2" data-id="element-1071">
            Toelichting (optioneel)
          </label>
          <textarea id="message" name="message" value={formData.message} onChange={handleChange} rows={3} className="w-full px-4 py-3 rounded-md border border-white/30 bg-white/10 focus:outline-none focus:ring-2 focus:ring-[#CDA735] focus:border-transparent text-white placeholder-white/50" placeholder="Heeft u nog specifieke wensen of vragen?" data-id="element-1072"></textarea>
        </div>
        {/* Captcha */}
        <div className="p-4 rounded-lg bg-white/10" data-id="element-1073">
          <label className="block text-white/90 font-medium mb-2" data-id="element-1074">
            Verificatie *
          </label>
          <div className="flex items-center space-x-4" data-id="element-1075">
            <div className="bg-white/20 p-3 rounded border border-white/30 flex-grow max-w-[180px]" data-id="element-1076">
              <p className="text-white font-medium" data-id="element-1077">{captcha.question}</p>
            </div>
            <input type="text" value={captchaAnswer} onChange={handleCaptchaChange} required className="w-20 px-3 py-3 rounded-md border border-white/30 bg-white/10 focus:outline-none focus:ring-2 focus:ring-[#CDA735] focus:border-transparent text-white text-center" placeholder="?" data-id="element-1078" />
            <button type="button" onClick={refreshCaptcha} className="p-2 text-white/70 hover:text-white transition-colors" aria-label="Vernieuw captcha" data-id="element-1079">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-1080">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" data-id="element-1081" />
              </svg>
            </button>
          </div>
          <p className="mt-2 text-sm text-white/70 flex items-center" data-id="element-1082">
            <Shield className="h-4 w-4 mr-1" data-id="element-1083" />
            Anti-spam maatregel
          </p>
        </div>
        <div className="bg-white/10 p-4 rounded-lg" data-id="element-1084">
          <div className="flex items-start" data-id="element-1085">
            <div className="flex items-center h-5 mt-1" data-id="element-1086">
              <input id="consent" name="consent" type="checkbox" required checked={formData.consent} onChange={handleChange} className="w-5 h-5 text-[#CDA735] bg-white/20 border-2 border-white/50 rounded focus:ring-[#CDA735] focus:ring-offset-0 cursor-pointer checked:bg-[#CDA735] checked:border-[#CDA735]" data-id="element-1087" />
            </div>
            <div className="ml-3 text-sm" data-id="element-1088">
              <span className="text-white/90 font-medium block mb-1 group-hover:text-white transition-colors" data-id="element-1089">
                Ik ga akkoord met de verwerking van mijn gegevens
              </span>
              <p className="text-white/70" data-id="element-1090">
                Veranova gebruikt uw gegevens alleen om contact met u op te
                nemen over uw offerteaanvraag en zal deze niet delen met derden.
                Lees onze{' '}
                <a href="#" className="text-[#CDA735] hover:text-[#B89429] underline-offset-2 hover:underline focus:underline focus:outline-none" data-id="element-1091">
                  privacyverklaring
                </a>
                .
              </p>
            </div>
          </div>
        </div>
      </div>
      <InfoBox icon={<ShieldCheck data-id="element-1093" />} data-id="element-1092">
        Uw privacy is belangrijk voor ons. We gebruiken uw gegevens alleen voor
        het behandelen van uw aanvraag en nemen binnen 48 uur contact met u op.
      </InfoBox>
      <FormButtons onPrevious={goToPreviousStep} onNext={validateAndProceed} isNextDisabled={!formData.name || !formData.email || !formData.phone || !formData.consent || !captchaAnswer} data-id="element-1094" />
    </FormStep>;
};